<template>
    <div class="spin-the-wheel__timer--small" v-if="remainingTime > 0">
        {{ formattedTime }}
    </div>
</template>



<script setup lang="ts">

import { SpinTheWheelStore } from '@/store/SpinTheWheelStore';
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';

const props = defineProps({
    endTime: {
        type: Number,
        required: true
    }
});

const spinTheWheelStore = SpinTheWheelStore();
const currentTimeSeconds = ref<number>(Math.floor(new Date().getTime() / 1000));
const timerInterval = ref<number>(1000); // default timer interval is 1 second
let timer: number | null = null;



// Calculating the remaining time based on the end time and current time
const remainingTime = computed((): number => {
	const endTime = props.endTime;
	if (!endTime) return 0;
	const remaining = endTime - currentTimeSeconds.value;
	return remaining > 0 ? remaining : 0;
});



const formattedTime = computed((): string => {
	const remaining = remainingTime.value;
	
    // Format the remaining time to hours and minutes if the remaining time is greater than 1 hour
	if (remaining >= 3600) {
		const hours = Math.floor(remaining / 3600);
		const minutes = Math.floor((remaining % 3600) / 60);
		return `${hours}h : ${minutes}m`;
	}
	
    // Format the remaining time to minutes if the remaining time is greater than 120 seconds
	if (remaining >= 60) {
		const minutes = Math.floor(remaining / 60);
		const seconds = remaining % 60;
		return `${minutes}m : ${seconds}s`;
	}
	
    // Format the remaining time to seconds if the remaining time is less than 120 seconds
	return `${remaining}s`;
});



// Start and stop the timer when the component is mounted
onMounted(() => {
	timer = window.setInterval(() => {
		currentTimeSeconds.value = Math.floor(new Date().getTime() / 1000);
	}, timerInterval.value);
});

onUnmounted(() => {
	if (timer !== null) {
		clearInterval(timer);
	}
});



// Watcher to handle remainingTime changes and adjust interval accordingly
watch(remainingTime, async (newValue: number): Promise<void> => {
    

    // Set the timer interval to 1 minute if the remaining time is greater than 120 seconds otherwise set it to 1 second
    if(newValue > 3600){
        timerInterval.value = 60000;
    } else {
        timerInterval.value = 1000;
    }

    // Clear the timer if it is already running and set a new timer with the new interval
    if (timer !== null) {
        clearInterval(timer);
    }
    timer = window.setInterval(() => {
        currentTimeSeconds.value = Math.floor(new Date().getTime() / 1000);
    }, timerInterval.value);


    // Fetch the initial data when timer is over, just in case if there is a bug in the timer or there is a new update in the backend.
	if (newValue <= 0) {
		await spinTheWheelStore.getWheelInitialData();
        await spinTheWheelStore.getActiveWheelRewards();
	}
});

</script>